import React, { useEffect, useState } from 'react';
import './ThresholdApprovalPopup.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/free-solid-svg-icons';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
// Strip trailing /api to avoid double /api/api in URLs
const BASE_URL = (API_BASE_URL || 'http://localhost:5000').replace(/\/api\/?$/, '');

const ThresholdApprovalPopup = ({ onClose }) => {
  const [pendingRequests, setPendingRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [businessId, setBusinessId] = useState(null);
  const [userId, setUserId] = useState(null);
  const [processingId, setProcessingId] = useState(null);
  const [reviewNotes, setReviewNotes] = useState({});
  const [fullImageModal, setFullImageModal] = useState({ open: false, url: '', itemName: '' });
  const [imageLoadErrors, setImageLoadErrors] = useState({});

  useEffect(() => {
    const loadData = async () => {
      try {
        const userStr = window.localStorage.getItem('user');
        if (!userStr) {
          return;
        }
        
        const user = JSON.parse(userStr);
        const resolvedUserId = user.id || user.user_id || user.userId;
        setUserId(resolvedUserId);
        
        // Get business_id from localStorage (stored separately during login)
        const storedBusinessId = localStorage.getItem('businessId');
        const effectiveBusinessId = storedBusinessId || user.business_id || user.businessId;
        setBusinessId(effectiveBusinessId);
        

        if (!resolvedUserId) {
          return;
        }

        const userResponse = await fetch(`${BASE_URL}/api/users/${resolvedUserId}`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'X-Business-Id': String(effectiveBusinessId)
          }
        });
        
        if (!userResponse.ok) {
          return;
        }
        
        const userData = await userResponse.json();
        const roleName = userData.data?.role_name;
        
        // Check if user is owner/admin using .includes() to handle "Owner/Admin"
        const isOwnerOrAdmin = roleName && (roleName.toLowerCase().includes('owner') || roleName.toLowerCase().includes('admin'));
        
        if (!isOwnerOrAdmin) {
          return;
        }
        

        // Fetch pending approval requests
        const url = `${BASE_URL}/api/wastage/threshold-approvals/pending?userId=${resolvedUserId}`;

        if (!effectiveBusinessId) {
          console.error('[ThresholdApprovalPopup] ❌ No business_id available!');
          return;
        }
        
        const response = await fetch(url, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'X-Business-Id': String(effectiveBusinessId)
          }
        });

        if (response.ok) {
          const result = await response.json();

          if (result.success && result.data && Array.isArray(result.data)) {
            setPendingRequests(result.data);
            } else {
            setPendingRequests([]);
          }
        } else {
          const errorText = await response.text();
          console.error('[ThresholdApprovalPopup] Failed to fetch:', response.status, errorText);
          setPendingRequests([]);
        }
      } catch (error) {
        console.error('[ThresholdApprovalPopup] Error loading approval requests:', error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  const handleApprove = async (approvalId) => {
    setProcessingId(approvalId);
    try {
      const response = await fetch(
        `${BASE_URL}/api/wastage/threshold-approvals/${approvalId}/approve`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json',
            'X-Business-Id': businessId
          },
          body: JSON.stringify({
            userId: userId,
            review_notes: reviewNotes[approvalId] || 'Approved via quick action'
          })
        }
      );

      if (response.ok) {
        // Remove from list
        setPendingRequests(prev => prev.filter(r => r.approval_id !== approvalId));
      } else {
        const result = await response.json();
        console.error('Failed to approve:', result.error || 'Unknown error');
      }
    } catch (error) {
      console.error('Error approving:', error);
    } finally {
      setProcessingId(null);
    }
  };

  const handleReject = async (approvalId) => {
    const notes = reviewNotes[approvalId];
    if (!notes?.trim()) {
      alert('Please add review notes before rejecting.');
      return;
    }

    setProcessingId(approvalId);
    try {
      const response = await fetch(
        `${BASE_URL}/api/wastage/threshold-approvals/${approvalId}/reject`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json',
            'X-Business-Id': businessId
          },
          body: JSON.stringify({
            userId: userId,
            review_notes: notes
          })
        }
      );

      if (response.ok) {
        setPendingRequests(prev => prev.filter(r => r.approval_id !== approvalId));
      } else {
        const result = await response.json();
        console.error('Failed to reject:', result.error || 'Unknown error');
      }
    } catch (error) {
      console.error('Error rejecting:', error);
    } finally {
      setProcessingId(null);
    }
  };

  // Don't show popup if still loading or not an owner
  if (loading) {
    return null;
  }

  // Don't show popup if no pending requests (Dashboard already checks this, but extra safety)
  if (pendingRequests.length === 0) {
    return null;
  }

  return (
    <div className="threshold-popup-overlay" onClick={onClose}>
      <div className="threshold-popup-container" onClick={(e) => e.stopPropagation()}>
        <div className="threshold-popup-header">
          <div>
            <h3>Approval Required</h3>
          </div>
          <button className="threshold-popup-close" onClick={onClose}>×</button>
        </div>

        <div className="threshold-popup-content">
          {pendingRequests.map((request) => {
            // Extract timestamp from justification if it exists
            let displayTime = new Date(request.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: true });
            
            if (request.justification) {
              // Try to extract timestamp from justification like "Waste recorded via mobile app - 12/28/2025, 3:18:40 PM"
              const timestampMatch = request.justification.match(/(\d{1,2}\/\d{1,2}\/\d{4},\s*\d{1,2}:\d{2}:\d{2}\s*(?:AM|PM))/i);
              if (timestampMatch) {
                displayTime = new Date(timestampMatch[1]).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: true });
              }
            }
            
            return (
            <div key={request.approval_id} className="threshold-popup-request">
              {/* Header: Item name on left, timestamp on right */}
              <div className="request-header">
                <strong className="request-item-name">{request.item_name}</strong>
                <div className="request-meta">
                  <span className="request-time">{displayTime}</span>
                </div>
              </div>

              {/* Image Section */}
              {(() => {
                // Collect all images (for now, we have one image, but structure supports multiple)
                const images = [];
                if (request.evidence_image_id || request.evidence_image_url) {
                  images.push({
                    id: request.evidence_image_id,
                    url: request.evidence_image_url
                  });
                }
                
                if (images.length === 0) return null;
                
                // Determine layout class based on image count
                let layoutClass = 'request-images-container';
                if (images.length === 1) layoutClass += ' single-image';
                else if (images.length === 2) layoutClass += ' two-images';
                else if (images.length === 3) layoutClass += ' three-images';
                else layoutClass += ' multiple-images';
                
                return (
                  <div className={layoutClass}>
                    {images.map((image, idx) => (
                      <div key={idx} className="request-item-image">
                        {imageLoadErrors[request.approval_id] ? (
                          <div className="image-error">📸 Image not accessible</div>
                        ) : (
                          <img 
                            src={
                              image.id 
                                ? `${BASE_URL}/api/wastage/photos/proxy/${image.id}` 
                                : (image.url?.startsWith('http') ? image.url : `${BASE_URL}${image.url}`)
                            }
                            alt={`Wastage evidence ${idx + 1} for ${request.item_name}`}
                            title="Click to view full image"
                            onClick={() => {
                              const imageUrl = image.id 
                                ? `${BASE_URL}/api/wastage/photos/proxy/${image.id}` 
                                : (image.url?.startsWith('http') ? image.url : `${BASE_URL}${image.url}`);
                              setFullImageModal({ 
                                open: true, 
                                url: imageUrl, 
                                itemName: request.item_name 
                              });
                            }}
                            style={{ cursor: 'pointer' }}
                            onLoad={() => {
                              setImageLoadErrors(prev => {
                                if (!prev[request.approval_id]) return prev;
                                const next = { ...prev };
                                delete next[request.approval_id];
                                return next;
                              });
                            }}
                            onError={(e) => { 
                              console.error('[ThresholdApprovalPopup] ❌ Image failed to load');
                              console.error('   Item:', request.item_name);
                              console.error('   Image ID:', image.id);
                              console.error('   Image URL:', image.url);
                              console.error('   Attempted URL:', e.target?.src);
                              setImageLoadErrors(prev => ({ ...prev, [request.approval_id]: true }));
                            }}
                          />
                        )}
                      </div>
                    ))}
                  </div>
                );
              })()}
              
              {/* Details Section */}
              <div className="request-details">
                {/* Recorded by line - First */}
                <div className="recorded-by-line"><span style={{fontWeight:600}}>Recorded by:</span> {request.requested_by_name}</div>
                
                {/* Wastage Amount - Second */}
                {request.threshold_type === 'wastage_review' ? (
                  <div className="request-change wastage-review-inline">
                    <span className="wastage-label">Wastage Amount: {parseInt(request.requested_threshold)} {request.threshold_unit || 'plates'} <span className="threshold-info">(Threshold: {parseInt(request.current_threshold)})</span></span>
                  </div>
                ) : (
                  <div className="request-change">
                    <span className="old-value">{request.current_threshold ? parseInt(request.current_threshold) : 'Not Set'}</span>
                    <span className="arrow">→</span>
                    <span className="new-value">{parseInt(request.requested_threshold)} {request.threshold_unit || 'plates'}</span>
                  </div>
                )}
                
                {/* Reason - Third */}
                {(() => {
                  const justification = String(request.justification || '');
                  const lower = justification.toLowerCase();
                  const reasonIdx = lower.indexOf('reason:');
                  let reasonText = reasonIdx >= 0 ? justification.slice(reasonIdx).trim() : justification.trim();
                  
                  // Remove "Reason:" prefix if present
                  let actualReason = reasonText;
                  if (reasonText.toLowerCase().startsWith('reason:')) {
                    actualReason = reasonText.slice(7).trim();
                  }
                  
                  // Check if it's only the automatic "Waste recorded via..." text without user reason
                  const isAutoGenerated = /^waste recorded via mobile app/i.test(actualReason) || 
                                         /^waste recorded via/i.test(actualReason);
                  
                  if (isAutoGenerated || !actualReason) {
                    return (
                      <div className="reason-line" style={{textAlign:"left"}}>
                        <strong>Reason:</strong> No Reason has been provided
                      </div>
                    );
                  }
                  
                  return (
                    <div className="reason-line" style={{textAlign:"left"}}>
                      <strong>Reason:</strong> {actualReason}
                    </div>
                  );
                })()}

                <textarea
                  className="request-notes"
                  placeholder="Add review notes..."
                  value={reviewNotes[request.approval_id] || ''}
                  onChange={(e) => setReviewNotes(prev => ({
                    ...prev,
                    [request.approval_id]: e.target.value
                  }))}
                  disabled={processingId === request.approval_id}
                  rows={2}
                />
              </div>

              <div className="request-actions">
                <button
                  className="btn-reject-popup"
                  onClick={() => handleReject(request.approval_id)}
                  disabled={processingId === request.approval_id}
                >
                  {processingId === request.approval_id ? '...' : 'Reject'}
                </button>
                <button
                  className="btn-approve-popup"
                  onClick={() => handleApprove(request.approval_id)}
                  disabled={processingId === request.approval_id}
                >
                  {processingId === request.approval_id ? '...' : 'Approve'}
                </button>
              </div>
            </div>
            );
          })}
        </div>
      </div>

      {/* Full Image View */}
      {fullImageModal.open && (
        <div 
          className="image-modal-overlay" 
          onClick={(e) => {
            e.stopPropagation();
            setFullImageModal({ open: false, url: '', itemName: '' });
          }}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: '#000',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000
          }}
        >
          {/* Close Button */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              setFullImageModal({ open: false, url: '', itemName: '' });
            }}
            style={{
              position: 'fixed',
              top: '20px',
              right: '20px',
              background: 'transparent',
              color: '#fff',
              border: 'none',
              borderRadius: '8px',
              padding: '12px',
              fontSize: '28px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: 'none',
              zIndex: 10001
            }}
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
          
          {/* Full Image */}
          <img
            src={fullImageModal.url}
            alt={`Full size wastage evidence for ${fullImageModal.itemName}`}
            style={{
              maxWidth: '100%',
              maxHeight: '100vh',
              width: 'auto',
              height: 'auto',
              objectFit: 'contain'
            }}
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
};

export default ThresholdApprovalPopup;
